import React, { useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import { dashboardApis, fetchDashboardTable, mrRateApis, fetchRtWindForecast } from "./api";
import "./dashboard.css";
import { RiFullscreenFill } from "react-icons/ri";
import { FaCommentsDollar, FaDownload } from "react-icons/fa6";
import { FaChartBar } from "react-icons/fa";
import { FaTableCells } from "react-icons/fa6";
import { renderChart } from "./renderChart";
import RenderTable from './renderTable';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const Dashboard = ({ date, onFullscreenClick }) => {
  const getTodayInIST = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().split("T")[0];
  };
  const [selectedDate, setSelectedDate] = useState(date || getTodayInIST());
  const solarRef = useRef();
  const windRef = useRef();
  const loadRef = useRef();
  const [selectedLoadModel] = useState(() => localStorage.getItem("selectedLoadModel") || "");
  const [selectedSolarModel] = useState(() => localStorage.getItem("selectedSolarModel") || "");
  const [selectedWindModel] = useState(() => localStorage.getItem("selectedWindModel") || "");
  const [tableData, setDashboardTableData] = useState([]);
  const [solarData, setSolarData] = useState([]);
  const [windData, setWindData] = useState([]);
  const [loadData, setLoadData] = useState([]);
  const [solarRow, setSolarRow] = useState([]);
  const [windRow, setWindRow] = useState([]);
  const [loadRow, setLoadRow] = useState([]);
  const [currentHeader, setCurrentHeader] = useState([]);
  const [showChartView, setShowChartView] = useState({
    solar: true,
    wind: true,
    load: true,
  });

  const [solarHeaders, setSolarHeaders] = useState([]);
  const [solarTableRows, setSolarTableRows] = useState([]);
  const [windHeaders, setWindHeaders] = useState([]);
  const [windTableRows, setWindTableRows] = useState([]);
  const [loadHeaders, setLoadHeaders] = useState([]);
  const [loadTableRows, setLoadTableRows] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const isCurrentDate = selectedDate === getTodayInIST();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (date) {
      setDashboardTableData([]);
      setCurrentHeader([]);
      setSolarHeaders([]);
      setSolarTableRows([]);
      setWindHeaders([]);
      setWindTableRows([]);
      setLoadHeaders([]);
      setLoadTableRows([]);
      setSolarData([]);
      setWindData([]);
      setLoadData([]);
      setSolarRow([]);
      setWindRow([]);
      setLoadRow([]);
      setShowAlert(false);
      setSelectedDate(date);
    }
  }, [date]);

  useEffect(() => {
    if (solarRef.current) solarRef.current.innerHTML = "";
    if (windRef.current) windRef.current.innerHTML = "";
    if (loadRef.current) loadRef.current.innerHTML = "";

    fetchAllMRRateForecasts(selectedDate);
    fetchCurrentMRRateForecasts(selectedDate);

    const intervalId = setInterval(() => {
      if (solarRef.current) solarRef.current.innerHTML = "";
      if (windRef.current) windRef.current.innerHTML = "";
      if (loadRef.current) loadRef.current.innerHTML = "";

      fetchAllMRRateForecasts(selectedDate);
      fetchCurrentMRRateForecasts(selectedDate);
    }, 15 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [selectedDate]);

  useEffect(() => {
    if (solarRef.current && showChartView.solar) {
      solarRef.current.innerHTML = "";
      renderChart(solarData, solarRef.current, "Solar_Forecast", selectedDate);
    }
    if (windRef.current && showChartView.wind) {
      windRef.current.innerHTML = "";
      renderChart(windData, windRef.current, "Wind_Forecast", selectedDate);
    }
    if (loadRef.current && showChartView.load) {
      loadRef.current.innerHTML = "";
      renderChart(loadData, loadRef.current, "Load_Forecast", selectedDate);
    }
  }, [showChartView]);

  useEffect(() => {
    const diffExists = hasAnyDifference(currentHeader, loadRow, solarRow, windRow, tableData);
    setShowAlert(diffExists);
  }, [currentHeader, loadRow, solarRow, windRow, tableData]);

  //   const fetchAllMRRateForecasts = async (date) => {
  //   const storedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
  //   const loadRate = storedRates["Load Forecast"] ?? 0.2;
  //   const solarRate = storedRates["Solar Forecast"] ?? 0.2;
  //   const windRate = storedRates["Wind Forecast"] ?? 0.2;

  //   const roundedLoadRate = loadRate;
  //   const roundedSolarRate = solarRate;
  //   const roundedWindRate = windRate;

  //   const loadModel = selectedLoadModel || "";
  //   const solarModel = selectedSolarModel || "";
  //   const windModel = selectedWindModel || "";

  //   const currentDate = new Date().toISOString().split("T")[0];
  //   const selectedDateStr = new Date(date).toISOString().split("T")[0];

  //   try {
  //     const results = await Promise.allSettled([
  //       dashboardApis["Solar Forecast"](date, roundedSolarRate, solarModel),
  //       selectedDateStr < currentDate
  //         ? fetchRtWindForecast(date)
  //         : dashboardApis["Wind Forecast"](date, roundedWindRate, windModel),
  //       dashboardApis["Load Forecast"](date, roundedLoadRate, loadModel),
  //     ]);

  //     if (results[0].status === "fulfilled") {
  //       const solar = results[0].value.data;
  //       setSolarData(solar);
  //       renderChart(solar, solarRef.current, "Solar_Forecast", selectedDate);
  //       const { headers, rows } = prepareTableData(solar, selectedDate);
  //       setSolarHeaders(headers);
  //       setSolarTableRows(rows);
  //     } else {
  //       //console.error("Solar Forecast API failed:", results[0].reason);
  //     }

  //     if (results[1].status === "fulfilled") {
  //       const wind = results[1].value.data;
  //       setWindData(wind);
  //       renderChart(wind, windRef.current, "Wind_Forecast", selectedDate);
  //       const { headers, rows } = prepareTableData(wind, selectedDate);
  //       setWindHeaders(headers);
  //       setWindTableRows(rows);
  //     } else {
  //       //console.error("Wind Forecast API failed:", results[1].reason);
  //     }

  //     if (results[2].status === "fulfilled") {
  //       const load = results[2].value.data;
  //       setLoadData(load);
  //       renderChart(load, loadRef.current, "Load_Forecast", selectedDate);
  //       const { headers, rows } = prepareTableData(load, selectedDate);
  //       setLoadHeaders(headers);
  //       setLoadTableRows(rows);
  //     } else {
  //     //  console.error("Load Forecast API failed:", results[2].reason);
  //     }
  //   } catch (err) {
  //     //console.error("Unexpected error in forecast API fetch:", err);
  //   }

  //   try {
  //     const response = await fetchDashboardTable();
  //     setDashboardTableData(response.data);
  //   } catch (err) {
  //     //console.error("Error fetching dashboard table data:", err);
  //   }
  // };


 const fetchAllMRRateForecasts = async (date) => {
  setLoading(true); // ✅ Start loading bar

  try {
    const storedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
    const loadRate = storedRates["Load Forecast"] ?? 0.2;
    const solarRate = storedRates["Solar Forecast"] ?? 0.2;
    const windRate = storedRates["Wind Forecast"] ?? 0.2;

    const loadModel = selectedLoadModel || "";
    const solarModel = selectedSolarModel || "";
    const windModel = selectedWindModel || "";

    const currentDate = new Date().toISOString().split("T")[0];
    const selectedDateStr = new Date(date).toISOString().split("T")[0];

    await Promise.all([
      dashboardApis["Solar Forecast"](date, solarRate, solarModel)
        .then(res => {
          const solar = res.data;
          setSolarData(solar);
          renderChart(solar, solarRef.current, "Solar_Forecast", selectedDate);
          const { headers, rows } = prepareTableData(solar, selectedDate);
          setSolarHeaders(headers);
          setSolarTableRows(rows);
        }),

      (selectedDateStr < currentDate
        ? fetchRtWindForecast(date)
        : dashboardApis["Wind Forecast"](date, windRate, windModel)
      ).then(res => {
        const wind = res.data;
        setWindData(wind);
        renderChart(wind, windRef.current, "Wind_Forecast", selectedDate);
        const { headers, rows } = prepareTableData(wind, selectedDate);
        setWindHeaders(headers);
        setWindTableRows(rows);
      }),

      dashboardApis["Load Forecast"](date, loadRate, loadModel)
        .then(res => {
          const load = res.data;
          setLoadData(load);
          renderChart(load, loadRef.current, "Load_Forecast", selectedDate);
          const { headers, rows } = prepareTableData(load, selectedDate);
          setLoadHeaders(headers);
          setLoadTableRows(rows);
        }),

      fetchDashboardTable().then(res => setDashboardTableData(res.data)),
    ]);
  } catch (err) {
    console.error("Forecast API error:", err);
  } finally {
    setLoading(false);
  }
};

  const getCurrentBlockNo = () => {
    const now = dayjs().tz("Asia/Kolkata");
    const hour = now.hour();
    const minute = now.minute();
    const currentBlock = hour * 4 + Math.ceil(minute / 15);
    return Math.min(currentBlock + 1, 96);
  };

  const filterNext10Blocks = (data, startBlock) => {
    if (!data || !data.length) return [];

    const filtered = [];
    for (let i = 0; i < 10; i++) {
      const block = startBlock + i;
      if (block > 96) break;

      const match = data.find(d => Number(d.block_no) === block);
      if (match) filtered.push(match);
    }

    return filtered;
  };

  const blockNoToTime = (blockNo) => {
    const totalMinutes = (blockNo - 1) * 15;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  };


  const fetchCurrentMRRateForecasts = async (date) => {

    const storedRates = JSON.parse(localStorage.getItem("mrRates")) || {};
    const loadRate = storedRates["Load Forecast"] ?? 0.2;
    const solarRate = storedRates["Solar Forecast"] ?? 0.2;
    const windRate = storedRates["Wind Forecast"] ?? 0.2;

    const roundedLoadRate = loadRate;
    const roundedSolarRate = solarRate;
    const roundedWindRate = windRate;

    const loadModel = selectedLoadModel || "";
    const solarModel = selectedSolarModel || "";
    const windModel = selectedWindModel || "";

    try {
      const results = await Promise.allSettled([
        mrRateApis["Solar Forecast"](date, roundedSolarRate, solarModel),
        mrRateApis["Wind Forecast"](date, roundedWindRate, windModel),
        mrRateApis["Load Forecast"](date, roundedLoadRate, loadModel),
      ]);

      const currentBlock = getCurrentBlockNo();

      let solarFiltered = [], windFiltered = [], loadFiltered = [];

      if (results[0].status === "fulfilled") {
        solarFiltered = filterNext10Blocks(results[0].value.data, currentBlock);
      } else {
        // console.error("Solar Forecast API failed:", results[0].reason);
      }

      if (results[1].status === "fulfilled") {
        windFiltered = filterNext10Blocks(results[1].value.data, currentBlock);
      } else {
        //  console.error("Wind Forecast API failed:", results[1].reason);
      }

      if (results[2].status === "fulfilled") {
        loadFiltered = filterNext10Blocks(results[2].value.data, currentBlock);
      } else {
        // console.error("Load Forecast API failed:", results[2].reason);
      }

      const headers = solarFiltered.map(d => blockNoToTime(d.block_no));
      setCurrentHeader(headers);
      const demand = loadFiltered.map(d => Math.round(parseFloat(d.forecast) || 0));
      setLoadRow(demand);
      const solar = solarFiltered.map(d => Math.round(parseFloat(d.forecast) || 0));
      setSolarRow(solar);
      const wind = windFiltered.map(d => Math.round(parseFloat(d.forecast) || 0));
      setWindRow(wind);

    } catch (err) {
      //console.error("Unexpected error:", err);
    }
  };

  const prepareTableData = (data, date) => {
    if (!Array.isArray(data) || data.length === 0) {
      // console.error("Invalid or empty data format");
      return { headers: [], rows: [] };
    }

    const uniqueHeaders = new Set(["Date", "Block No", "Datetime", "Rev No", "RT_FORECAST"]);
    const processedData = {};
    const columnDataTypes = {
      "Date": "date",
      "Block No": "number",
      "Datetime": "datetime",
      "Rev No": "number",
      "RT_FORECAST": "number",
    };
    const nonEditableColumns = new Set(["Date", "Block No"]);

    data.forEach((row) => {
      const { date: rowDate, datetime, block_no, model_name, forecast, rev_no } = row;

      if (model_name !== "RT_FORECAST" || !rowDate || !block_no || forecast === undefined) {
        return;
      }

      const formattedDate = new Date(rowDate).toISOString().split("T")[0];
      const formattedDatetime = new Date(datetime).toISOString();

      if (date && formattedDate !== date) {
        return;
      }

      let finalForecast = forecast;
      if (finalForecast === null) {
        finalForecast = "null";
      } else if (!isNaN(finalForecast)) {
        finalForecast = Math.round(parseFloat(finalForecast));
      }

      const rowKey = `${formattedDate}-${block_no}`;
      if (!processedData[rowKey]) {
        processedData[rowKey] = {
          Date: formattedDate,
          "Block No": block_no,
          Datetime: formattedDatetime,
          "Rev No": rev_no,
          RT_FORECAST: finalForecast,
        };
      } else {
        processedData[rowKey]["RT_FORECAST"] = finalForecast;
      }
    });

    const headers = Array.from(uniqueHeaders).map((header) => ({
      name: header,
      dataType: columnDataTypes[header] || "string",
      editable: !nonEditableColumns.has(header),
      width: 150,
      displayName: header,
    }));

    const allRows = Object.values(processedData);
    if (allRows.length === 0) return { headers: [], rows: [] };

    let targetDate = date;
    if (!targetDate) {
      targetDate = allRows.reduce((min, row) => {
        return row["Date"] < min ? row["Date"] : min;
      }, allRows[0]["Date"]);
    }

    const rows = allRows
      .filter((row) => row["Date"] === targetDate)
      .sort((a, b) => {
        const dateA = new Date(a["Date"]);
        const dateB = new Date(b["Date"]);
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return (a["Block No"] || 0) - (b["Block No"] || 0);
      })
      .map((row, idx) => ({ id: idx, ...row }));

    return { headers, rows };
  };

  const renderForecastTable = (tableData) => {
    const timeWithDate = {};
    tableData.forEach(({ time, datetime }) => {
      if (!timeWithDate[time] || new Date(datetime) < new Date(timeWithDate[time])) {
        timeWithDate[time] = datetime;
      }
    });

    const uniqueTimes = Object.keys(timeWithDate).sort((a, b) => {
      return new Date(timeWithDate[a]) - new Date(timeWithDate[b]);
    });

    const uniqueSeries = [...new Set(tableData.map((d) => d.series_name))];

    const forecastMap = {};
    tableData.forEach(({ series_name, time, forecast }) => {
      if (!forecastMap[series_name]) forecastMap[series_name] = {};
      forecastMap[series_name][time] = Math.round(parseFloat(forecast) || 0);
    });

    const labelMap = {
      'Demand': 'Demd',
      'Solar-gen': 'Solar',
      'Wind-gen': 'Wind'
    };

    return (
      <table className="forecast-table">
        <thead>
          <tr>
            <th>Fcst</th>
            {uniqueTimes.map((t) => (
              <th key={t}>{t}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {uniqueSeries.map((series) => (
            <tr key={series}>
              <td>{labelMap[series] ?? series}</td>
              {uniqueTimes.map((t) => (
                <td key={t}>{forecastMap[series][t] ?? 0}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderMRForecastTable = (headers, loadRow, solarRow, windRow, tableData) => {
    const originalDemandMap = {};
    const originalSolarMap = {};
    const originalWindMap = {};

    tableData.forEach(d => {
      const val = Math.round(parseFloat(d.forecast) || 0);
      if (d.series_name === "Demand") {
        originalDemandMap[d.time] = val;
      } else if (d.series_name === "Solar-gen") {
        originalSolarMap[d.time] = val;
      } else if (d.series_name === "Wind-gen") {
        originalWindMap[d.time] = val;
      }
    });

    const labelMap = {
      Demand: "Demd",
      "Solar-gen": "Solar",
      "Wind-gen": "Wind",
    };

    const getCellStyle = (originalMap, time, value) => {
      const original = originalMap[time];
      const isDifferent = original !== undefined && Math.abs(value - original) > 200;
      return {
        backgroundColor: isDifferent ? "#dc143c" : "transparent",
        color: isDifferent ? "white" : "inherit"
      };
    };

    return (
      <table className="forecast-table">
        <thead>
          <tr>
            <th>Cur Fcst</th>
            {headers.map(t => (
              <th key={t}>{t}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{labelMap["Demand"]}</td>
            {headers.map((time, i) => (
              <td key={time} style={getCellStyle(originalDemandMap, time, loadRow[i])}>
                {loadRow[i]}
              </td>
            ))}
          </tr>
          <tr>
            <td>{labelMap["Solar-gen"]}</td>
            {headers.map((time, i) => (
              <td key={time} style={getCellStyle(originalSolarMap, time, solarRow[i])}>
                {solarRow[i]}
              </td>
            ))}
          </tr>
          <tr>
            <td>{labelMap["Wind-gen"]}</td>
            {headers.map((time, i) => (
              <td key={time} style={getCellStyle(originalWindMap, time, windRow[i])}>
                {windRow[i]}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  };

  const hasAnyDifference = (headers, loadRow, solarRow, windRow, tableData) => {
    const originalDemandMap = {};
    const originalSolarMap = {};
    const originalWindMap = {};

    tableData.forEach(d => {
      const val = Math.round(parseFloat(d.forecast) || 0);
      if (d.series_name === "Demand") originalDemandMap[d.time] = val;
      else if (d.series_name === "Solar-gen") originalSolarMap[d.time] = val;
      else if (d.series_name === "Wind-gen") originalWindMap[d.time] = val;
    });

    return headers.some((time, i) => {
      const demandDiff = originalDemandMap[time] !== undefined && Math.abs(loadRow[i] - originalDemandMap[time]) > 200;
      const solarDiff = originalSolarMap[time] !== undefined && Math.abs(solarRow[i] - originalSolarMap[time]) > 200;
      const windDiff = originalWindMap[time] !== undefined && Math.abs(windRow[i] - originalWindMap[time]) > 200;
      return demandDiff || solarDiff || windDiff;
    });
  };

  const downloadCSV = (data, fileName) => {
    if (!Array.isArray(data) || data.length === 0) return;

    const filteredData = data
      .filter(row => row.model_name === "RT_FORECAST")
      .map(row => ({
        Date: new Date(row.date).toISOString().split("T")[0],
        "Block No": row.block_no,
        Datetime: new Date(row.datetime).toISOString(),
        "Rev No": row.rev_no,
        RT_FORECAST: row.forecast,
      }));

    if (filteredData.length === 0) return;

    const headers = ["Date", "Block No", "Datetime", "Rev No", "RT_FORECAST"];
    const csvRows = [
      headers.join(","),
      ...filteredData.map(row =>
        headers.map(field => `"${(row[field] ?? "").toString().replace(/"/g, '""')}"`).join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetDashboardState = () => {
    setSolarHeaders([]);
    setSolarTableRows([]);
    setWindHeaders([]);
    setWindTableRows([]);
    setLoadHeaders([]);
    setLoadTableRows([]);
    setSolarData([]);
    setWindData([]);
    setLoadData([]);
    setSolarRow([]);
    setWindRow([]);
    setLoadRow([]);
    setDashboardTableData([]);
    setCurrentHeader([]);
  };

  return (
    <div>
      {loading && (
  <div className="dashboard-loading-bar"></div>
)}
      <div className="top-bar">
        <div className="date-picker">
          <input
            id="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => {
              resetDashboardState();
              setSelectedDate(e.target.value);
            }}
          />
          <label htmlFor="date-input">Date</label>
        </div>
      </div>
      <div>
        {showAlert && (
          <div
            style={{
              position: "fixed",
              top: "120px",
              right: "140px",
              backgroundColor: "#dc143c",
              color: "white",
              padding: "10px 18px",
              borderRadius: "9999px",
              fontWeight: "500",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              gap: "12px",
              maxWidth: "380px",
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            <span style={{ flex: 1 }}>
              There is a{" "}
              <strong style={{ fontWeight: "bold", color: "#ffffcc" }}>
                significant difference
              </strong>{" "}
              between the present prediction and the real-time forecast. Please refer to highlighted cells.
              <br />
              <strong style={{ color: "#fff" }}>Note:</strong>{" "}
              <span style={{ fontWeight: 400 }}>Real-time forecasts have less latency.</span>
            </span>

            <button
              onClick={() => setShowAlert(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
                cursor: "pointer",
                lineHeight: 1,
                padding: 0,
                margin: 0,
              }}
              aria-label="Close alert"
            >
              ×
            </button>
          </div>
        )}
      </div>
      <div className="forecast-grid">
        {[
          { title: "Solar Forecast", ref: solarRef, data: solarData, filename: "Solar_RT_Forecast" },
          { title: "Wind Forecast", ref: windRef, data: windData, filename: "Wind_RT_Forecast" },
          { title: "Load Forecast", ref: loadRef, data: loadData, filename: "Load_RT_Forecast" },
        ].map(({ title, ref, data, filename }) => (
          <div key={title} className="chart-box" style={{ position: "relative" }}>
            <RiFullscreenFill
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                cursor: "pointer",
                fontSize: "20px",
                color: "#333",
                zIndex: 10,
              }}
              onClick={() => onFullscreenClick(title, selectedDate)}
              title={`Go to ${title} Fullscreen / Forecasting`}
            />

            <FaDownload
              style={{
                position: "absolute",
                top: "5px",
                right: "40px",
                cursor: "pointer",
                fontSize: "20px",
                color: "#333",
                zIndex: 10,
              }}
              title={`Download ${filename}.csv`}
              onClick={() => downloadCSV(data, filename)}
            />

            <h3>{title}</h3>

            {showChartView[title.toLowerCase().split(" ")[0]] ? (
              <>
                <FaTableCells
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "75px",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#333",
                    zIndex: 10,
                  }}
                  title="Switch to Table View"
                  onClick={() =>
                    setShowChartView((prev) => ({
                      ...prev,
                      [title.toLowerCase().split(" ")[0]]: false,
                    }))
                  }
                />
                <div ref={ref}></div>
              </>
            ) : (
              <>
                <FaChartBar
                  style={{
                    position: "absolute",
                    top: "5px",
                    right: "75px",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#333",
                    zIndex: 10,
                  }}
                  title="Switch to Chart View"
                  onClick={() =>
                    setShowChartView((prev) => ({
                      ...prev,
                      [title.toLowerCase().split(" ")[0]]: true,
                    }))
                  }
                />
                <RenderTable
                  headers={
                    title.includes("Solar")
                      ? solarHeaders
                      : title.includes("Wind")
                        ? windHeaders
                        : loadHeaders
                  }
                  rows={
                    title.includes("Solar")
                      ? solarTableRows
                      : title.includes("Wind")
                        ? windTableRows
                        : loadTableRows
                  }
                />
              </>
            )}
          </div>
        ))}
        {tableData.length > 0 && (
          <div className="chart-box" style={{ position: "relative" }}>

            {selectedDate !== getTodayInIST() && (
              <div
                style={{
                  backgroundColor: "#fffae6",
                  color: "#b36b00",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  marginBottom: "10px",
                  fontSize: "14px",
                  border: "1px solid #ffd591",
                }}
              >
                ⚠️ This table always displays the current day’s forecast, regardless of the selected date above
              </div>
            )}

            <div className="forecast-table">
              {renderForecastTable(tableData)}
            </div>
            <div className="forecast-table">
              {renderMRForecastTable(currentHeader, loadRow, solarRow, windRow, tableData)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;